import Cookies from 'js-cookie'
import { resetServiceAvailability } from './serviceAvailability'

export const SESSION_CHANGED = 'SESSION_CHANGED'
export const SESSION_EVENT_KEY = 'hug.session-ended'
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'

const listeners = new Set()
let session = { generation: 0, token: Cookies.get('access_token'), reason: null, ending: false }
let lastValidatedAt = 0

export const getSession = () => session
export const subscribeSession = (listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const publish = (changes) => {
  session = { ...session, ...changes }
  listeners.forEach(listener => listener())
}

export const isCurrentSession = (context) => (
  context.generation === session.generation && context.token === Cookies.get('access_token')
)

export const sessionChangedError = () => Object.assign(new Error('The session changed.'), {
  name: 'AbortError',
  sessionHandled: true,
})

export const assertCurrentSession = (context) => {
  if (!isCurrentSession(context) || session.ending) {
    if (context.generation === session.generation && !session.ending) checkSession()
    throw sessionChangedError()
  }
}

export const startSession = (token) => {
  lastValidatedAt = 0
  publish({ token, reason: null, ending: false, generation: session.generation + 1 })
}

export const endSession = (reason, broadcast = true) => {
  resetServiceAvailability()
  if (!session.token && session.reason === reason) return
  Cookies.remove('access_token', { path: '/' })
  lastValidatedAt = 0
  publish({ token: undefined, reason, ending: false, generation: session.generation + 1 })
  if (broadcast) {
    try {
      localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ reason, id: `${Date.now()}-${Math.random()}` }))
    } catch (_) {
      // Cookie checks still synchronize tabs when browser storage is unavailable.
    }
  }
}

export const expireSession = (context = session) => {
  if (!session.ending && context.token && isCurrentSession(context)) endSession('expired')
}

export const beginLogout = () => publish({ ending: true })

export const completeLogout = (context) => {
  if (context.generation !== session.generation) return
  const token = Cookies.get('access_token')
  if (token && token !== context.token) startSession(token)
  else endSession('logout')
}

export const isTokenExpired = (token, now = Date.now()) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const { exp } = JSON.parse(atob(payload))
    return typeof exp === 'number' && exp * 1000 <= now
  } catch (_) {
    // Only the server validates token authenticity and unsupported token formats.
    return false
  }
}

export const checkSession = () => {
  if (session.ending) return session
  const token = Cookies.get('access_token')
  if (!token && session.token) endSession('expired')
  else if (token && isTokenExpired(token)) {
    if (token !== session.token) startSession(token)
    endSession('expired')
  } else if (token !== session.token) startSession(token)
  return session
}

export const handleSessionStorage = (event) => {
  if (event.key !== SESSION_EVENT_KEY || !event.newValue) return
  try {
    const { reason } = JSON.parse(event.newValue)
    // A newer login shares its cookie with this tab; an older logout must not remove it.
    if (['logout', 'expired'].includes(reason) && !Cookies.get('access_token')
      && (session.token || (reason === 'logout' && session.reason === 'expired'))) {
      endSession(reason, false)
    }
  } catch (_) {
    // Ignore unrelated or malformed browser-storage messages.
  }
}

export const markSessionValidated = (context) => {
  if (isCurrentSession(context)) lastValidatedAt = Date.now()
}

export const shouldValidateSession = () => Date.now() - lastValidatedAt >= 5 * 60 * 1000
